/** React Query hooks for live onboarding BFF reads (no fixture fallback). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  fetchOnboardingSources,
  fetchOnboardingStatus,
  fetchOnboardingWarehouse,
  postOnboardingWarehouseDelete,
} from "@/services/apis/onboardingApi";
import type { OnboardingStatusResponse } from "@/types/onboarding.types";

export const ONBOARDING_QUERY_ROOT = "onboarding" as const;

export const onboardingQueryKeys = {
  status: (companyId: number | undefined) =>
    [ONBOARDING_QUERY_ROOT, companyId, "status"] as const,
  warehouse: (companyId: number | undefined) =>
    [ONBOARDING_QUERY_ROOT, companyId, "warehouse"] as const,
  sources: (companyId: number | undefined) =>
    [ONBOARDING_QUERY_ROOT, companyId, "sources"] as const,
};

/** Retry only what can recover: no response or a 5xx, twice at most. 4xx (403) is final. */
export function shouldRetryOnboardingRequest(
  failureCount: number,
  error: unknown,
): boolean {
  if (failureCount >= 2) {
    return false;
  }
  if (!isAxiosError(error)) {
    return false;
  }
  const status = error.response?.status;
  return status === undefined || status >= 500;
}

/**
 * Re-check status every minute while a company that finished the wizard waits on CS
 * readiness — only with the chat gate on. With it off nothing on screen changes when
 * the company turns ready, so polling would only cost a request per tab per minute.
 */
export function onboardingStatusRefetchInterval(
  status: OnboardingStatusResponse | undefined,
  chatGateEnabled: boolean,
): number | false {
  return chatGateEnabled && status?.wizardCompleted && !status.chatAllowed ? 60_000 : false;
}

export function useOnboardingStatusQuery(
  enabled: boolean,
  companyId: number | undefined,
  chatGateEnabled: boolean,
) {
  return useQuery({
    queryKey: onboardingQueryKeys.status(companyId),
    queryFn: fetchOnboardingStatus,
    enabled: enabled && companyId != null,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: shouldRetryOnboardingRequest,
    refetchInterval: (query) =>
      onboardingStatusRefetchInterval(
        query.state.data as OnboardingStatusResponse | undefined,
        chatGateEnabled,
      ),
  });
}

export interface OnboardingQueryOptions {
  /** Default true. The setup wizard refetches itself on return from Pulse/Daton instead. */
  refetchOnWindowFocus?: boolean;
}

export function useOnboardingWarehouseQuery(
  enabled: boolean,
  companyId: number | undefined,
  options?: OnboardingQueryOptions,
) {
  return useQuery({
    queryKey: onboardingQueryKeys.warehouse(companyId),
    queryFn: fetchOnboardingWarehouse,
    enabled: enabled && companyId != null,
    staleTime: 30_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
    retry: shouldRetryOnboardingRequest,
  });
}

export function useOnboardingSourcesQuery(
  enabled: boolean,
  companyId: number | undefined,
  options?: OnboardingQueryOptions,
) {
  return useQuery({
    queryKey: onboardingQueryKeys.sources(companyId),
    queryFn: () => fetchOnboardingSources(),
    enabled: enabled && companyId != null,
    staleTime: 30_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
    retry: shouldRetryOnboardingRequest,
  });
}

/** Delete a Daton warehouse, then refetch every onboarding read that depends on it. */
export function useDeleteOnboardingWarehouseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postOnboardingWarehouseDelete,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [ONBOARDING_QUERY_ROOT] }),
  });
}
